import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { DELETION_KIND, REACTION_KIND, isLikeReaction } from '@/lib/nip25';
import { WIKI_ARTICLE_KIND, isValidWikiArticle, parseAddressPointer, type AddressPointer } from '@/lib/nip54';

/**
 * Wiki articles a given pubkey has starred — i.e. the NIP-25 "like"
 * reactions (`kind:7`, `content: '+'`) they've published tagging a
 * `kind:30818` wiki article, honoring their own NIP-09 deletions as
 * "unstars" (see `useStars`/`StarButton`, the single-article
 * equivalent). Powers the "Starred articles" section on `ProfilePage`.
 *
 * Reactions are filtered server-side with `#k: ['30818']` (NIP-25's `k`
 * tag records the target event's kind), so this only ever fetches the
 * user's *article* stars — not their publication-index or
 * publication-section stars — in one relay round trip rather than
 * fetching every reaction and filtering client-side.
 */
export function useStarredArticlesByAuthor(pubkey: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['wiki', 'starred-by', pubkey],
    queryFn: async (c) => {
      if (!pubkey) return [];
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(6000)]);

      const reactions = await nostr.query(
        [{ kinds: [REACTION_KIND], authors: [pubkey], '#k': [String(WIKI_ARTICLE_KIND)], limit: 300 }],
        { signal },
      );
      const likes = reactions.filter(isLikeReaction);
      if (likes.length === 0) return [];

      // Honor the user's own NIP-09 deletions ("unstars") of these reactions.
      const deletions = await nostr.query(
        [{ kinds: [DELETION_KIND], authors: [pubkey], '#e': likes.map((r) => r.id), limit: 300 }],
        { signal },
      );
      const deletedIds = new Set(deletions.flatMap((d) => d.tags.filter(([n]) => n === 'e').map(([, v]) => v)));
      const active = likes.filter((r) => !deletedIds.has(r.id));
      if (active.length === 0) return [];

      // Resolve each reaction's `a` tag (always present for these — see
      // `buildStarTags`, which adds it for any addressable target) back
      // to the specific author's version of the article that was
      // starred. A given article may have been (re-)starred more than
      // once; keep only the most recent reaction per target.
      const byTarget = new Map<string, { pointer: AddressPointer; starredAt: number }>();
      for (const reaction of active) {
        const aTag = reaction.tags.find(([n]) => n === 'a')?.[1];
        if (!aTag) continue;
        const pointer = parseAddressPointer(aTag);
        if (!pointer || pointer.kind !== WIKI_ARTICLE_KIND) continue;
        const key = `${pointer.pubkey}:${pointer.identifier}`;
        const existing = byTarget.get(key);
        if (!existing || reaction.created_at > existing.starredAt) {
          byTarget.set(key, { pointer, starredAt: reaction.created_at });
        }
      }
      if (byTarget.size === 0) return [];

      const targets = [...byTarget.values()];
      const authors = [...new Set(targets.map((t) => t.pointer.pubkey))];
      const identifiers = [...new Set(targets.map((t) => t.pointer.identifier))];

      const events = await nostr.query(
        [{ kinds: [WIKI_ARTICLE_KIND], authors, '#d': identifiers, limit: targets.length * 3 + 20 }],
        { signal },
      );
      const valid = events.filter(isValidWikiArticle);

      const byKey = new Map<string, NostrEvent>();
      for (const event of valid) {
        const d = event.tags.find(([n]) => n === 'd')?.[1] ?? '';
        const key = `${event.pubkey}:${d}`;
        const existing = byKey.get(key);
        if (!existing || event.created_at > existing.created_at) byKey.set(key, event);
      }

      // Newest star first — missing targets (e.g. the article was never
      // fetched successfully, or has since vanished from every relay we
      // asked) are silently dropped rather than shown as broken cards.
      return targets
        .sort((a, b) => b.starredAt - a.starredAt)
        .map(({ pointer }) => byKey.get(`${pointer.pubkey}:${pointer.identifier}`))
        .filter((event): event is NostrEvent => Boolean(event));
    },
    enabled: Boolean(pubkey),
    staleTime: 30_000,
  });
}
